import { api, type ApiResponse } from './api';

/**
 * 업종(KSIC) 선택지 — 서버 `GET /industries`·`GET /industries/:id/path`(`apps/server/src/industries/`).
 * 현장 업종 picker(`components/sites/IndustryPicker.tsx`)가 쓴다.
 *
 * ⚠️ 이 모듈을 `lib/organizations`에 합치지 말 것 — 그 모듈을 `vi.mock`하는 spec이 많아 새 export가
 * 생기면 무관한 spec이 `No "X" export is defined on the mock`으로 죽는다.
 */
export interface IndustryOption {
  id: string;
  code: string;
  name: string;
  /** 1=대분류 … 5=세세분류(KSIC 11차) */
  level: number;
  /** 활성 자식이 있나 — false면 가장 세세한 분류다(picker가 더 내려가지 않는다) */
  hasChildren: boolean;
}

export const industriesQueryKeys = {
  children: (parentId: string | null) => ['industries', 'children', parentId ?? 'root'] as const,
  search: (query: string) => ['industries', 'search', query] as const,
  path: (id: string) => ['industries', 'path', id] as const,
};

function unwrap<T>(body: ApiResponse<T>): T {
  if (!body.success || body.data === null) throw body;
  return body.data;
}

/** 한 단계의 활성 업종 — `parentId`가 null이면 대분류 */
export async function getIndustryChildren(parentId: string | null): Promise<IndustryOption[]> {
  const { data } = await api.get<ApiResponse<IndustryOption[]>>('/industries', {
    params: parentId ? { parentId } : {},
  });
  return unwrap(data);
}

/** 코드·이름 검색 — 서버가 세세분류를 먼저 돌려준다 */
export async function searchIndustries(query: string): Promise<IndustryOption[]> {
  const { data } = await api.get<ApiResponse<IndustryOption[]>>('/industries', { params: { search: query } });
  return unwrap(data);
}

/** 대분류부터 그 업종까지 */
export async function getIndustryPath(id: string): Promise<IndustryOption[]> {
  const { data } = await api.get<ApiResponse<IndustryOption[]>>(`/industries/${encodeURIComponent(id)}/path`);
  return unwrap(data);
}

/** 업종 단계 이름 — KSIC 11차 */
export const INDUSTRY_LEVEL_LABELS: Record<number, string> = {
  1: '대분류',
  2: '중분류',
  3: '소분류',
  4: '세분류',
  5: '세세분류',
};
